import type { DateString, DateTimeString, SortDirection } from "@/types/common"
import type { Therapy } from "@/types/therapy"
import type { Visit, VisitsList } from "@/types/visit"
import { API_ROOT, HttpClient } from "@/api/httpClient"
import { dPayload } from "@/utils/dayjs"

export type VisitsListSortKey = "date"

export interface VisitsListQuery {
	perPage: number
	page: number
	sort?: {
		key?: VisitsListSortKey
		direction?: SortDirection
	}
	filters?: {
		/** cerca in: sintomi, note */
		text?: string
		dateFrom?: Date
		dateTo?: Date
		doctorId?: number
	}
}

interface VisitsListParams {
	paginate: true
	per_page: number
	page: number
	sort_key?: VisitsListSortKey
	sort_dir?: SortDirection
	searchTerm?: string
	/** YYYY-MM-DD */
	dateFrom?: DateString
	/** YYYY-MM-DD */
	dateTo?: DateString
	doctor?: number
}

export type TherapyPayload = Omit<Therapy, "id" | "created_at" | "updated_at">

export interface VisitPayload {
	/** YYYY-MM-DD HH:mm:ss */
	datetime: DateTimeString
	weight?: number
	blood_pressure?: string
	symptoms?: string
	prescribed_exams?: string[]
	prescribed_drugs?: string[]
	therapies?: TherapyPayload[]
	laboratory_exams?: string[]
	notes?: string
}

export default {
	list(patientId: number, payload?: VisitsListQuery) {
		const params: VisitsListParams = {
			paginate: true,
			per_page: payload?.perPage || 50,
			page: payload?.page || 1,
			sort_dir: payload?.sort?.direction || "desc",
			sort_key: payload?.sort?.key || "date"
		}

		if (payload?.filters?.doctorId !== undefined) {
			params.doctor = payload.filters.doctorId
		}

		if (payload?.filters?.text !== undefined) {
			params.searchTerm = payload.filters.text
		}

		if (payload?.filters?.dateFrom !== undefined && payload?.filters?.dateTo !== undefined) {
			params.dateFrom = dPayload(payload.filters.dateFrom, "date")
			params.dateTo = dPayload(payload.filters.dateTo, "date")
		}

		return HttpClient.get<VisitsList>(`${API_ROOT}/patients/${patientId}/medical-visits`, {
			params
		})
	},
	create(patientId: number, payload: VisitPayload) {
		return HttpClient.post<Visit>(`${API_ROOT}/patients/${patientId}/medical-visits`, payload)
	},
	update(patientId: number, visitId: number, payload: VisitPayload) {
		return HttpClient.put(`${API_ROOT}/patients/${patientId}/medical-visits/${visitId}`, payload)
	},
	details(patientId: number, visitId: number) {
		return HttpClient.get<Visit>(`${API_ROOT}/patients/${patientId}/medical-visits/${visitId}`)
	},
	delete(patientId: number, visitId: number) {
		return HttpClient.delete(`${API_ROOT}/patients/${patientId}/medical-visits/${visitId}`)
	}
}
